// 'use client' — ensures the open/closed status is recomputed in the browser with the current time
'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import { getStoreOpeningStatus } from '../lib/store-opening-hours';
import { formatTime } from '../lib/date-utils';
import type { OpeningHours } from '../lib/types';
import { useIsClient } from '@/product-discovery/hooks/useIsClient';
type Props = {
  openingHours: OpeningHours;
};
export function StoreOpenStatus(t0) {
  const $ = _c(24);
  const {
    openingHours
  } = t0;
  const t = useTranslations("Stores");
  const tDaysInSentence = useTranslations("daysInSentence");
  const hydrated = useIsClient();
  if (!hydrated) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <StoreOpenStatusSkeleton />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  let t1;
  let t2;
  if ($[1] !== openingHours || $[2] !== t || $[3] !== tDaysInSentence) {
    t2 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const status = getStoreOpeningStatus(openingHours);
      if (status.status === "open") {
        let t3;
        if ($[6] !== t) {
          t3 = t("open");
          $[6] = t;
          $[7] = t3;
        } else {
          t3 = $[7];
        }
        let t4;
        if ($[8] !== t3) {
          t4 = <span className="ut-label-fixed-sm text-positive">{t3}</span>;
          $[8] = t3;
          $[9] = t4;
        } else {
          t4 = $[9];
        }
        const t5 = status.closesAt ? <><StatusSeparator /><span className="text-secondary">{t.rich("closesAt", {
              time: formatTime(status.closesAt),
              t: chunks => <time dateTime={status.closesAt}>{chunks}</time>
            })}</span></> : null;
        let t6;
        if ($[10] !== t4 || $[11] !== t5) {
          t6 = <p className="ut-body-fixed-sm">{t4}{t5}</p>;
          $[10] = t4;
          $[11] = t5;
          $[12] = t6;
        } else {
          t6 = $[12];
        }
        t2 = t6;
        break bb0;
      }
      if (status.status === "openingSoon") {
        let t3;
        if ($[13] !== t) {
          t3 = t("openingSoon");
          $[13] = t;
          $[14] = t3;
        } else {
          t3 = $[14];
        }
        let t4;
        if ($[15] !== t3) {
          t4 = <span className="ut-label-fixed-sm text-yellow-600">{t3}</span>;
          $[15] = t3;
          $[16] = t4;
        } else {
          t4 = $[16];
        }
        const t5 = status.opensAt ? <><StatusSeparator /><span className="text-secondary">{t.rich("opensAt", {
              day: "today",
              time: formatTime(status.opensAt),
              t: chunks_0 => <time dateTime={status.opensAt}>{chunks_0}</time>
            })}</span></> : null;
        let t6;
        if ($[17] !== t4 || $[18] !== t5) {
          t6 = <p className="ut-body-fixed-sm">{t4}{t5}</p>;
          $[17] = t4;
          $[18] = t5;
          $[19] = t6;
        } else {
          t6 = $[19];
        }
        t2 = t6;
        break bb0;
      }
      let t3;
      if ($[20] !== t) {
        t3 = t("closed");
        $[20] = t;
        $[21] = t3;
      } else {
        t3 = $[21];
      }
      let t4;
      if ($[22] !== t3) {
        t4 = <span className="ut-label-fixed-sm text-negative">{t3}</span>;
        $[22] = t3;
        $[23] = t4;
      } else {
        t4 = $[23];
      }
      t1 = <p className="ut-body-fixed-sm">{t4}{status.opensAt ? <><StatusSeparator /><span className="text-secondary">{t.rich("opensAt", {
              day: status.opensTomorrow ? "tomorrow" : "other",
              weekday: status.opensOnDay ? tDaysInSentence(status.opensOnDay) : "",
              time: formatTime(status.opensAt),
              t: chunks_1 => <time dateTime={status.opensAt}>{chunks_1}</time>
            })}</span></> : null}</p>;
    }
    $[1] = openingHours;
    $[2] = t;
    $[3] = tDaysInSentence;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  if (t2 !== Symbol.for("react.early_return_sentinel")) {
    return t2;
  }
  return t1;
}
function StoreOpenStatusSkeleton() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <p className="ut-body-fixed-sm"><span className="ut-label-fixed-sm">{"\xA0"}</span><span className="text-secondary">{"\xA0"}</span></p>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function StatusSeparator() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <span className="text-secondary px-1.5" aria-hidden={true}>·</span>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}